import { Injectable } from '@angular/core';

/**
 * RefreshSchedulerService
 * 
 * Purpose: Prevents bugs related to ticket reservations for past events/dates.
 * 
 * Problem: When the app runs continuously for more than 1 day without refresh,
 * users might encounter issues trying to book tickets for dates that have already passed.
 * 
 * Current Solution: Schedule a full app refresh daily at 1am (when app usage is minimal)
 * to ensure fresh data and prevent date-related bugs.
 * 
 * Future Enhancement: Ideally, we would implement a smarter refresh mechanism that:
 * - Detects user inactivity before refreshing
 * - Only refreshes when the user is not actively interacting with the kiosk
 * 
 * For now, a simple daily refresh at 1am is sufficient to avoid these problems.
 */
@Injectable({
  providedIn: 'root'
})
export class RefreshSchedulerService {
  private refreshTimeoutId?: number;
  private readonly DAILY_REFRESH_HOUR = 1; //1am
  private readonly DAILY_REFRESH_MINUTES = 0;

  scheduleDailyRefresh(): void {
    const now = new Date();
    const nextDailyRefreshTime = new Date();
    
    //set to daily refresh time
    nextDailyRefreshTime.setHours(this.DAILY_REFRESH_HOUR, this.DAILY_REFRESH_MINUTES, 0, 0);
    
    //if daily refresh time has already passed today, schedule for tomorrow
    if (now >= nextDailyRefreshTime) {
      nextDailyRefreshTime.setDate(nextDailyRefreshTime.getDate() + 1);
    }
    
    //calculate milliseconds until next daily refresh time
    const timeUntilRefresh = nextDailyRefreshTime.getTime() - now.getTime();
    
    //clear any existing timeout (to avoid multiple scheduled refreshes)
    if (this.refreshTimeoutId) {
      clearTimeout(this.refreshTimeoutId);
    }
    
    //schedule the refresh
    this.refreshTimeoutId = window.setTimeout(() => {
      this.performRefresh();
    }, timeUntilRefresh);
    
    console.log(`App refresh scheduled for: ${nextDailyRefreshTime.toLocaleString()}`);
  }

  private performRefresh(): void {
    console.log('Starting scheduled app refresh');
    window.location.href = '/index.html';
  }
}

